import { useState, Fragment } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  ReviewCardContainer,
} from '../../../components/application/defaultComponents'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import { useRenewal } from 'src/context/renewal'
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom'
import Button from 'src/components/basic/Button'
import { IoMdCheckmark } from 'react-icons/io'
import { ReviewData, ClaimData } from 'src/dtos/applicationData'
import StandardInput from 'src/components/basic/StandardInput'
import FilePicker from 'src/components/basic/FilePicker'
import { uploadFiles } from 'src/services/apiClient'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { isClaimsTriggered } from 'src/utils/renewals'
import { numberToCurrency } from 'src/utils/numberFormat'
import { TbFileCheck } from 'react-icons/tb'
import {FILE_SIZE_LABEL} from "../../../utils/constants";

const Questions = ({screenshotMode = false}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { renewalData, saveRenewalData } = useRenewal()
  const [review, setReview] = useState<ReviewData>(renewalData?.renewalSection?.reviewData ?? {})
  const [showNotesInput, setShowNotesInput] = useState(!!renewalData?.renewalSection?.reviewData?.note)
  const [showFilePicker, setShowFilePicker] = useState(!!renewalData?.renewalSection?.reviewData?.files)

  const handleEditClick = () => {
    navigate({
      pathname: '/customer/renew/questions/main',
      search: createSearchParams({ review: 'renewal' }).toString()
    })
  }

  const updatedApplication = () => ({
    ...renewalData,
    stoppedAt: pathname,
    renewalSection: {
      ...renewalData?.renewalSection,
      reviewData: review
    }
  })

  const handleSaveApplication = async () => {
    try {
      await saveRenewalData(updatedApplication())
    } catch (_) { }
  }

  const produceApplicationData = (key: keyof ReviewData, value: any) => ({
    ...renewalData,
    stoppedAt: pathname,
    renewalSection: {
      ...renewalData?.renewalSection,
      reviewData: {
        ...renewalData?.renewalSection?.reviewData,
        [key]: value
      }
    }
  })

  const handleRemoveNote = async () => {
    const removedNoteApplication = produceApplicationData('note', undefined)
    setReview(prev => ({ ...prev, note: undefined }))
    setShowNotesInput(false)
    await saveRenewalData(removedNoteApplication)
  }

  const handleUploadFiles = async (files: File[]) => {
    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)

      const updatedFiles = [...(review?.files ?? []), ...(uploadedFiles ?? [])]
  
      const applicationFileAttached = produceApplicationData('files', updatedFiles)
      
      setReview(prev => ({ ...prev, files: updatedFiles }))
      await saveRenewalData(applicationFileAttached)
    } else {
      setReview(prev => ({ ...prev, files: undefined }))
      const applicationFileAttached = produceApplicationData('files', undefined)
      await saveRenewalData(applicationFileAttached)
    }
  }

  const renewalSection = renewalData?.renewalSection
  const claims = renewalData?.renewalSection?.claims

  const QuestionsSection = () => (
    <section>
      <h5>QUESTIONS</h5>
      <article>
        <strong>
          Since your last application, has any firm member acted as “Of Counsel” or Independent Contract Attorney to another law firm?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasICOCAttorneys)}</span>
        {renewalSection?.hasICOCAttorneys === true ? (
          <span>{renewalSection?.hasICOCAttorneysDescription ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Since your last application, has the Firm or any Firm member filed any lawsuits against clients for fees?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasSueForFeesPolicy)}</span>
        {renewalSection?.hasSueForFeesPolicy === true ? (
          <span>{renewalSection?.hasSueForFeesPolicyDescription ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Since your last application, have any of the firm’s lawyers completed a TLIE sponsored CLE course?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasTlieSponsoredCle)}</span>
      </article>
      <article>
        <strong>
          Has the firm or any member been the subject of a legal malpractice claim in the past 10 years?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasMalPracticeClaims)}</span>
        {renewalSection?.hasMalPracticeClaims === true ? (
          <span><h6>Has the matter been previously reported to TLIE?:</h6> {handleYesNoAnswers(renewalSection?.hasMalPracticeClaimsReported)}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Does any firm member or employee (including non-attorneys) of the firm know, have reason to know, or have any basis to believe that a circumstance, act, error or omission might reasonably be expected to be the basis for a professional liability claim against the firm or any attorney?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasMalPracticeBasis)}</span>
        {renewalSection?.hasMalPracticeBasis === true ? (
          <span><h6>Has the matter been previously reported to TLIE?:</h6> {handleYesNoAnswers(renewalSection?.hasMalPracticeBasisReported)}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Has the Firm or any firm member, including attorneys who have joined the Firm, ever been the subject of a grievance complaint in the past five years?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasGrievanceComplain)}</span>
        {renewalSection?.hasGrievanceComplain === true ? (
          <span><h6>Has the matter been previously reported to TLIE?:</h6> {handleYesNoAnswers(renewalSection?.hasGrievanceComplainReported)}</span>
        ) : null}
        {renewalSection?.hasGrievanceComplainReported === false ? 
          renewalSection?.hasGrievanceComplainFile?.map(file => (
            <span key={file?.id} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}><TbFileCheck /> {file?.fileName ?? 'N/A'}</span>
          )) : null}
      </article>
      <article>
        <strong>
          Has any firm member ever voluntarily accepted discipline or been refused admission to the bar, reprimanded (privately or publicly), suspended from the practice of law, disbarred, received a probationary license or otherwise disciplined by any disciplinary or licensing authority?
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.hasVoluntarilyAcceptedDiscipline)}</span>
        {renewalSection?.hasVoluntarilyAcceptedDiscipline === true ? (
          <span>{renewalSection?.hasVoluntarilyAcceptedDisciplineDescription ?? 'N/A'}</span>
        ) : null}
      </article>
      <article>
        <strong>
          Would you like a separate quote for standalone cyber coverage from one of our partners? This is optional and separate from your Lawyers' Professional Liability quote.
        </strong>
        <span>{handleYesNoAnswers(renewalSection?.wantsStandaloneCyberQuote)}</span>
        {renewalSection?.wantsStandaloneCyberQuote === true ? (
          <>
            <span><h6>Estimated annual revenue:</h6></span>
            <span>{renewalSection?.cyberEstimatedAnnualRevenue != null ? numberToCurrency(renewalSection?.cyberEstimatedAnnualRevenue) : 'N/A'}</span>
            <span><h6>Total number of employees:</h6></span>
            <span>{renewalSection?.cyberTotalEmployees ?? 'N/A'}</span>
            <span><h6>Firm website:</h6></span>
            <span>{renewalSection?.cyberFirmWebsiteUrl ?? 'N/A'}</span>
          </>
        ) : null}
      </article>
    </section>
  )

  const ClaimsSection = ({ claim, index }: { claim: ClaimData, index: number }) => (
    <section>
      <h5>CLAIM {index + 1}</h5>
      <article>
        <strong>Firm Name</strong>
        <span>{claim?.firmName ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Individuals of firm involved in the claim</strong>
        {claim?.hasInvolvedMembers === false ? (
          <span>I'm not sure</span>
        ) : claim?.involvedMembers ? claim.involvedMembers.map(member => (
          <span key={member}>{member}</span>
        )) : <span>N/A</span>}
      </article>
      <article>
        <strong>Claimant’s Full Name</strong>
        <span>{claim?.hasClaimant === false ? "I'm not sure" : (claim?.claimantFullName ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Claim Type</strong>
        <span>{claim?.claimType ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Date of Claim</strong>
        <span>{claim?.hasClaimDate === false ? "I'm not sure" : (claim?.claimDate ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Date of Alleged Error</strong>
        <span>{claim?.hasAllegedErrorDate === false ? "I'm not sure" : (claim?.allegedErrorDate ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Is this claim closed?</strong>
        <span>{claim?.isClaimClosed ?? 'N/A'}</span>
        {claim?.isClaimClosed === 'Yes' ? (
          <>
            <span>
              <h6>Indicate how the claim was closed: </h6>
              {claim?.closingType ?? 'N/A'}
            </span>
            <span>
              <h6>Total loss paid including deductibles: </h6>
              {claim?.totalLossPaid ? numberToCurrency(claim?.totalLossPaid) : 'N/A'}
            </span>
            <span>
              <h6>Defense cost paid: </h6>
              {claim?.defenseCostPaid ? numberToCurrency(claim?.defenseCostPaid) : 'N/A'}
            </span>
          </>
        ) : claim?.isClaimClosed === 'No' ? (
          <>
            <span>
              <h6>Claimant’s settlement demand: </h6>
              {claim?.claimantsSettlementDemand ? numberToCurrency(claim?.claimantsSettlementDemand) : 'N/A'}
            </span>
            <span>
              <h6>Defendant’s offer for settlement: </h6>
              {claim?.defendantsSettlementOffer ? numberToCurrency(claim?.defendantsSettlementOffer) : 'N/A'}
            </span>
            <span>
              <h6>Insurer’s loss reserve: </h6>
              {claim?.insurersLossReserve ? numberToCurrency(claim?.insurersLossReserve) : 'N/A'}
            </span>
            <span>
              <h6>Deductible: </h6>
              {claim?.closingCaseDeductible ? numberToCurrency(claim?.closingCaseDeductible) : 'N/A'}
            </span>
            <span>
              <h6>Is claim in suit?: </h6>
              {claim?.isClaimInSuit ?? 'N/A'}
            </span>
            {claim?.isClaimInSuit === 'Yes' ? (
              <span>
                <h6>Amount asked in summons: </h6>
                {claim?.amountAskedSummons ? numberToCurrency(claim?.amountAskedSummons) : 'N/A'}
              </span>
            ) : null}
            <span>
              <h6>Total loss pain including deductibles: </h6>
              {claim?.totalLossPain ? numberToCurrency(claim?.totalLossPain) : 'N/A'}
            </span>
          </>
        ) : null}
      </article>
      <article>
        <strong>Name of Insurer</strong>
        <span>{claim?.hasInsurer === false ? "I'm not sure" : (claim?.insurerName ?? 'N/A')}</span>
      </article>
      <article>
        <strong>Description of Claim</strong>
        <span>
          <h6>Alleged act, error, or omission upon which claimant bases claim:</h6>
        </span>
        <span>{claim?.hasClaimDescription === false ? "I'm not sure" : (claim?.claimDescription ?? 'N/A')}</span>
        <span>
          <h6>Description of case and events, including area of practice out of which the claim arose:</h6>
        </span>
        <span>{claim?.hasCaseDescription === false ? "I'm not sure" : (claim?.caseDescription ?? 'N/A')}</span>
        <span>
          <h6>Description of the type and extent of injury or damage allegedly sustained:</h6>
        </span>
        <span>{claim?.hasDamageDescription === false ? "I'm not sure" : (claim?.damageDescription ?? 'N/A')}</span>
        <span>
          <h6>Upload any applicable claim information:</h6>
        </span>
        {claim?.claimFile?.map(file => (
          <span key={file?.id} style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}><TbFileCheck /> {file?.fileName ?? 'N/A'}</span>
        ))}
      </article>
    </section>
  )

  const ColumnsBuilder = () => {
    let sectionsArray = [
      <QuestionsSection />
    ]

    const leftColumn = sectionsArray.filter((section, index) => index % 2 === 0)
    const rightColumn = sectionsArray.filter((section, index) => index % 2 !== 0)

    return (
      <>
        <div className='column'>
          {leftColumn}
        </div>
        <div className='column'>
          {rightColumn}
        </div>
      </>
    )
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Almost done! Please review the renewal information before you sign.</DefaultTitle>
        <DefaultSubtitle>If any answers are innacurate or incomplete you can use the edit button to change them.</DefaultSubtitle>
        <ReviewCardContainer id='questionsData'>
          <header>
            <div>
              <h4>Renewal Questions</h4>
              {!screenshotMode && (
                <h6>
                  <IoMdCheckmark />
                  Complete
                </h6>
              )}
            </div>
            {!screenshotMode && (
              <div>
                <Button style={{ padding: '8px 46px' }} onClick={handleEditClick}>Edit Section</Button>
              </div>
            )}
          </header>
          <main className='firm'>
            {ColumnsBuilder()}
          </main>
          <main className='firm'>
            <div className='column'>
              {(isClaimsTriggered(renewalData) === true && claims && claims.length > 0) ? claims.map((claim, index) => {
                return index % 2 === 0 ? <ClaimsSection claim={claim} index={index} key={index} /> : null
              }) : null}
            </div>
            <div className='column'>
              {(isClaimsTriggered(renewalData) === true && claims && claims.length > 0) ? claims.map((claim, index) => {
                return index % 2 !== 0 ? <ClaimsSection claim={claim} index={index} key={index} /> : null
              }) : null}
            </div>
          </main>
          {!screenshotMode && (
            <footer>
              {showNotesInput ? (
                <div className="review-footer-note-container">
                  <StandardInput
                    label=""
                    rows={15}
                    value={review?.note ?? ''}
                    onChange={e => setReview(prev => ({ ...prev, note: e.target.value }))}
                  />
                  <div className="review-footer-note-buttons">
                    {!!renewalData?.firmSection?.review?.note ? (
                      <Button
                        variant="error"
                        onClick={handleRemoveNote}
                      >
                        Remove note
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="review-button-small-width"
                          onClick={handleSaveApplication}
                        >
                          Save details
                        </Button>
                        <Button
                          variant="error"
                          className="review-button-small-width"
                          onClick={() => {
                            setReview(prev => ({ ...prev, note: undefined }))
                            setShowNotesInput(false)
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowNotesInput(true)}
                  >
                    Add a note
                  </Button>
                </div>
              )}
              {showFilePicker ? (
                <div className='review-footer-filepicker-container'>
                  <FilePicker
                    title={FILE_SIZE_LABEL}
                    onFilesSelect={handleUploadFiles}
                    selectedFiles={review?.files ?? []}
                    accept='.doc,.docx,application/msword, application/pdf'
                    limit={10}
                  />
                </div>
              ) : (
                <div className="review-footer-button-container">
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilePicker(true)}
                  >
                    Attach a document
                  </Button>
                </div>
              )}
            </footer>
          )}
        </ReviewCardContainer>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute="/customer/renew/review/attorneys"
        nextRoute="/customer/renew/sign/signature"
        updatedApplication={updatedApplication()}
      />
    </ApplicationBodyContainer>
  )
}

export default Questions